import React, { useCallback, useEffect, useState } from 'react';

import { Text } from '@mantine/core';
import Table from 'components/Table';
import dayjs from 'dayjs';
import useOtherWorkEvents from 'hooks/otherWorkEvent/useOtherWorkEvents';
import { CellProps } from 'react-table';
import { OtherWorkEventListView } from 'types';

import WorkHoursEmptyState from './WorkHoursEmptyState';

interface IWorkHoursTableContainerProps {
  year: number;
  month: number;
  staffId?: string;
  canLogOnBehalf: boolean;
  onLogWorkClick: () => void;
}

const formatTime = (time: string): string => {
  if (!time) return '';
  return dayjs(time, 'HH:mm:ss').format('hh:mm A');
};

const WorkHoursTableContainer = ({
  year,
  month,
  staffId,
  canLogOnBehalf,
  onLogWorkClick,
}: IWorkHoursTableContainerProps): JSX.Element => {
  const [ordering, setOrdering] = useState('date');
  const [ascending, setAscending] = useState(false);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useOtherWorkEvents({
      year,
      month,
      staff: staffId,
      ordering: ascending ? ordering : `-${ordering}`,
    });

  const [tableData, setTableData] = useState<OtherWorkEventListView[]>([]);

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data]);

  const handleHeaderClick = useCallback(
    (header: string): void => {
      if (header === ordering) {
        setAscending(prev => !prev);
        return;
      }
      setOrdering(header);
      setAscending(false);
    },
    [ordering],
  );

  const columns = [
    {
      Header: 'Event',
      id: 'other_work_type__name',
      Cell: ({ row }: CellProps<OtherWorkEventListView>) => (
        <Text>{row.original.otherWorkTypeName ?? ''}</Text>
      ),
    },
    ...(canLogOnBehalf
      ? [
          {
            Header: 'Staff Member',
            id: 'staff_name',
            disableSortBy: true,
            Cell: ({ row }: CellProps<OtherWorkEventListView>) => (
              <Text>{row.original.staffName ?? ''}</Text>
            ),
          },
        ]
      : []),
    {
      Header: 'Duration',
      id: 'duration',
      disableSortBy: true,
      Cell: ({ row }: CellProps<OtherWorkEventListView>) => <Text>{row.original.duration} min</Text>,
    },
    {
      Header: 'Date',
      id: 'date',
      Cell: ({ row }: CellProps<OtherWorkEventListView>) => <Text>{row.original.date}</Text>,
    },
    {
      Header: 'Start',
      id: 'time',
      Cell: ({ row }: CellProps<OtherWorkEventListView>) => <Text>{formatTime(row.original.time)}</Text>,
    },
  ];

  return (
    <Table
      tableData={tableData}
      columns={columns}
      isSuccess={isSuccess}
      isLoading={isLoading}
      isError={isError}
      isFetchingNextPage={isFetchingNextPage}
      hasNextPage={!!hasNextPage}
      fetchNextPage={fetchNextPage}
      clickableHeaders={['other_work_type__name', 'date', 'time']}
      handleHeaderClick={handleHeaderClick}
      ordering={ordering}
      ascending={ascending}
      EmptyState={<WorkHoursEmptyState onLogWorkClick={onLogWorkClick} />}
    />
  );
};

export default WorkHoursTableContainer;
